import React, {FC, useState} from "react";
import {Box, Button, Popover, Typography} from "@material-ui/core";
import {useCurrentShowId} from "hooks";
import Badge from "@mui/material/Badge";
import NotificationsNoneIcon from "@material-ui/icons/NotificationsNone";
import {grey} from "@material-ui/core/colors";
import {apiV2} from "api/firebase";
import {useNotificationBar} from "store/notification-bar/notification-bar.store";
import InfiniteScroll from "react-infinite-scroll-component";

import NotificationList from "view/components/AppBar/components/NotificationsPopup/components/NotificationList";
import NotificationBarLoader from "view/components/AppBar/components/NotificationsPopup/NotificationLoader";
import FlexBox from "view/components/Layout/FlexBox";
import {useNotificationBarStatus} from "hooks/useNotificationBarStatus";
import useStyles from "./styles";

interface NotificationProps {
  organiserId: string;
}

const MIN_TO_LOAD = 15;

const Notification: FC<NotificationProps> = ({organiserId}) => {
  const classes = useStyles();
  const showId = useCurrentShowId();
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  const {
    isNewNotifications,
    isUnreadNotification,
    refetch: refetchNotificationBar,
  } = useNotificationBarStatus(showId, organiserId);

  const [inViewNotificationIds, setInViewNotificationIds] = useState<string[]>(
    []
  );

  const {
    data,
    pagination,
    fetch,
    reload,
    loadMore,
    autoCatch,
    isLoading,
  } = useNotificationBar();

  const notifications = data[showId] || [];
  const pageInfo = pagination[showId] || {page: 0, total: 0};
  const hasMore = notifications.length < (pageInfo.total || 0);

  const handlePopoverOpen = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElement(event.currentTarget);

    if (notifications.length === 0) {
      await fetch({showId, page: 1, limit: MIN_TO_LOAD});
    }

    autoCatch(showId);
  };

  const handlePopoverClose = async () => {
    setAnchorElement(null);

    // No active show → nothing is show-scoped to persist, and every
    // notification-bar endpoint is ACL-gated on a non-empty `showId` (a blank
    // one 403s "Resource ID is required: show"). Bail before any request.
    if (!showId) {
      setInViewNotificationIds([]);
      return;
    }

    if (inViewNotificationIds.length) {
      await apiV2.notificationBar.changeStatus({
        ids: inViewNotificationIds,
        showId,
      });
    }

    reload(showId);
    await refetchNotificationBar();
    setInViewNotificationIds([]);
  };

  const markAllNotificationsAsRead = async () => {
    try {
      await apiV2.notificationBar.markReadAll({
        showId,
      });
      setInViewNotificationIds([]);
      reload(showId);
      await refetchNotificationBar();
    } catch (e) {
      console.error(e);
    }
  };

  const markOneNotificationAsRead = async (notificationId: string) => {
    await apiV2.notificationBar.markRead({
      ids: [notificationId],
      showId,
    });
    reload(showId);
    await refetchNotificationBar();
  };

  const showEmptyState = !isLoading && notifications.length === 0;

  // Without an active show there is nothing show-scoped to render and every
  // notification-bar endpoint is ACL-gated on a non-empty `showId`. Render
  // nothing so a stale open popover can't fire a blank-`showId` request when
  // the show is deselected / navigated away from.
  if (!showId) {
    return null;
  }

  return (
    <>
      {!!showId && (
        <Badge
          color="error"
          overlap="circular"
          variant="dot"
          invisible={!isUnreadNotification}
          onClick={handlePopoverOpen}
        >
          <NotificationsNoneIcon
            htmlColor={grey[500]}
            width="24px"
            height="24px"
          />
        </Badge>
      )}
      <Popover
        open={Boolean(anchorElement)}
        anchorEl={anchorElement}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          className: classes.popoverPaper,
        }}
      >
        <Box>
          <Typography gutterBottom className={classes.notificationsTitle}>
            Notifications
          </Typography>
          <Box className={classes.divider} />

          {showEmptyState ? (
            <Box display="flex" justifyContent="center" alignItems="center">
              <Typography
                variant="body2"
                color="textSecondary"
                className={classes.noNotification}
              >
                You don’t have any notifications.
              </Typography>
            </Box>
          ) : (
            <InfiniteScroll
              dataLength={notifications.length}
              next={() => loadMore({showId, limit: MIN_TO_LOAD})}
              hasMore={hasMore}
              loader={<NotificationBarLoader />}
              height={400}
              className={classes.scrollContainer}
            >
              <NotificationList
                notifications={notifications}
                isNewNotifications={isNewNotifications}
                markOneNotificationAsRead={markOneNotificationAsRead}
                setInViewNotificationIds={setInViewNotificationIds}
                isLoading={isLoading}
              />
            </InfiniteScroll>
          )}

          {!showEmptyState && (
            <Box className={classes.dividerTop}>
              <FlexBox justifyContent="end">
                <Button
                  onClick={markAllNotificationsAsRead}
                  disabled={!isNewNotifications}
                  color="primary"
                  className={classes.markAllAsReadButton}
                >
                  Mark all as read
                </Button>
              </FlexBox>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default Notification;
